import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode, PointerEvent as ReactPointerEvent, RefObject } from 'react';
import { swipeIntent, swipeOffset, swipeRelease, type SwipeIntent } from './swipeMath';

export interface SwipeAction { label: string; icon: ReactNode; tone: 'ok' | 'gold' | 'grey' | 'danger'; onSelect: () => void }
export interface UseSwipeRowOptions {
  /** Fires on release past half the row. The row owns any completion
      animation (TaskRow's check pop) — the hook only slides and calls. */
  rightAction?: SwipeAction;
  leftActions?: SwipeAction[]; // max 3 — extra entries are ignored
  disabled?: boolean;
}
export interface SwipeRowState { offset: number; open: 'left' | null; dragging: boolean; settling: boolean; firing: boolean }
export interface SwipeRowBind {
  onPointerDown(e: ReactPointerEvent<HTMLElement>): void;
  onPointerMove(e: ReactPointerEvent<HTMLElement>): void;
  onPointerUp(e: ReactPointerEvent<HTMLElement>): void;
  onPointerCancel(e: ReactPointerEvent<HTMLElement>): void;
  onClickCapture(e: React.MouseEvent<HTMLElement>): void;
}

let closeOpenRow: (() => void) | null = null;
const closed: SwipeRowState = { offset: 0, open: null, dragging: false, settling: false, firing: false };

export function useSwipeRow(opts: UseSwipeRowOptions): { bind: SwipeRowBind; state: SwipeRowState; close: () => void; rowRef: RefObject<HTMLDivElement | null> } {
  const rowRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<SwipeRowState>(closed);
  const current = useRef(state);
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());
  const suppressClick = useRef(false);
  const gesture = useRef<{ id: number; x: number; y: number; openOffset: number; intent: SwipeIntent } | null>(null);
  const disabled = !!opts.disabled || (!opts.rightAction && !opts.leftActions?.length);

  const update = useCallback((next: SwipeRowState) => {
    current.current = next;
    setState(next);
  }, []);
  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current.clear();
  }, []);
  const later = useCallback((callback: () => void, delay: number) => {
    const timer = setTimeout(() => {
      timers.current.delete(timer);
      callback();
    }, delay);
    timers.current.add(timer);
  }, []);
  const close = useCallback(() => {
    clearTimers();
    gesture.current = null;
    if (closeOpenRow === close) closeOpenRow = null;
    update({ ...closed, settling: true });
    later(() => update(closed), 160);
  }, [clearTimers, later, update]);

  useEffect(() => {
    if (disabled) {
      clearTimers();
      gesture.current = null;
      suppressClick.current = false;
      if (closeOpenRow === close) closeOpenRow = null;
      update(closed);
    }
  }, [disabled, clearTimers, close, update]);

  useEffect(() => {
    if (disabled || state.open !== 'left') return;
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !rowRef.current?.contains(event.target)) close();
    };
    document.addEventListener('pointerdown', outside, true);
    return () => document.removeEventListener('pointerdown', outside, true);
  }, [disabled, state.open, close]);

  useEffect(() => () => {
    clearTimers();
    if (closeOpenRow === close) closeOpenRow = null;
  }, [clearTimers, close]);

  const geometry = () => ({ rowWidth: rowRef.current?.offsetWidth ?? 0, hasRight: !!opts.rightAction, leftCount: opts.leftActions?.length ?? 0 });
  const release = (e: ReactPointerEvent<HTMLElement>) => {
    const drag = gesture.current;
    if (disabled || e.pointerType === 'mouse' || !drag || drag.id !== e.pointerId) return;
    gesture.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    if (drag.intent !== 'horizontal') return;
    suppressClick.current = true;
    const geo = geometry();
    const result = swipeRelease(current.current.offset, geo);
    if (result.kind === 'fire-right') {
      if (closeOpenRow === close) closeOpenRow = null;
      // Slide fully across (160ms), fire, then slide back (160ms) — the
      // transition stays on for the whole round trip so the row never
      // sits parked off-screen or snaps.
      update({ ...closed, firing: true, settling: true, offset: geo.rowWidth });
      const action = opts.rightAction;
      later(() => {
        action?.onSelect();
        update({ ...closed, firing: true, settling: true });
        later(() => update(closed), 200);
      }, 160);
    } else if (result.kind === 'open-left') {
      if (closeOpenRow !== close) closeOpenRow?.();
      closeOpenRow = close;
      update({ ...closed, offset: result.offset, open: 'left', settling: true });
      later(() => update({ ...current.current, settling: false }), 160);
    } else {
      close();
    }
  };

  const bind: SwipeRowBind = {
    onPointerDown(e) {
      if (disabled || e.pointerType === 'mouse' || gesture.current || current.current.firing) return;
      clearTimers();
      suppressClick.current = false;
      gesture.current = { id: e.pointerId, x: e.clientX, y: e.clientY, openOffset: current.current.offset, intent: 'none' };
      update({ ...current.current, settling: false });
      // No capture yet: a plain tap must still target the row's own
      // handlers (the check, the open-sheet tap). Capture starts only once
      // the gesture commits to horizontal.
    },
    onPointerMove(e) {
      const drag = gesture.current;
      if (disabled || e.pointerType === 'mouse' || !drag || drag.id !== e.pointerId) return;
      const dx = e.clientX - drag.x;
      const before = drag.intent;
      drag.intent = swipeIntent(drag.intent, dx, e.clientY - drag.y);
      if (drag.intent !== 'horizontal') return;
      if (before !== 'horizontal') e.currentTarget.setPointerCapture(e.pointerId);
      update({ ...current.current, offset: swipeOffset(dx, drag.openOffset, geometry()), dragging: true, settling: false });
    },
    onPointerUp: release,
    // A cancelled gesture (browser took over for scrolling, palm rejection)
    // never fires an action — snap back only.
    onPointerCancel(e) {
      const drag = gesture.current;
      if (!drag || drag.id !== e.pointerId) return;
      gesture.current = null;
      if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
      suppressClick.current = false;
      if (drag.intent === 'horizontal') close();
    },
    onClickCapture(e) {
      if (disabled || !suppressClick.current) return;
      e.stopPropagation();
      e.preventDefault();
      suppressClick.current = false;
    },
  };
  return { bind, state: disabled ? closed : state, close, rowRef };
}
